import {
  SET_ICP_PROGRESS,
  SET_CAMPAIGN_PROGRESS,
  CLEAR_PROGRESS,
  CLEAR_ALL_PROGRESS,
} from "../constants/firebaseProgressConstants";

// State shape:
// {
//   "campaignId1": { icp: { completed, total, percentage, state }, campaign: { ... } },
//   "campaignId2": { icp: null, campaign: { ... } },
// }

const initialState = {};

// Value-equality for a single progress entry (icp or campaign). Firebase often
// re-emits an identical snapshot, and two listeners write the same campaign per
// tick; without this, every emission mints a new reducer ref and re-renders
// every subscriber (CampaignScreen -> the heavy CustomTable). Compare only the
// fields consumers actually read, so a NO-OP tick keeps the prior reference and
// a REAL change (different state/percentage/counts) still propagates.
// NOTE: the Firebase dispatchers emit exactly {completed,total,percentage,state}
// (or null). If a 5th field is ever added to that payload - or a consumer reads
// another field off the progress object - add it here too, or this bail-out
// will silently drop that update.
const isSameProgress = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false; // one null, other not -> changed
  return (
    a.state === b.state &&
    a.percentage === b.percentage &&
    a.completed === b.completed &&
    a.total === b.total
  );
};

export const firebaseProgressReducer = (state = initialState, action) => {
  switch (action.type) {
    case SET_ICP_PROGRESS: {
      const { campaignId, data } = action.payload;
      const existing = state[campaignId] || { icp: null, campaign: null };
      // No-op tick (re-emit / duplicate writer): keep the prior state ref so
      // subscribers don't re-render for a value that didn't change.
      if (isSameProgress(existing.icp, data)) return state;
      return {
        ...state,
        [campaignId]: {
          ...existing,
          icp: data,
        },
      };
    }

    case SET_CAMPAIGN_PROGRESS: {
      const { campaignId, data } = action.payload;
      const existing = state[campaignId] || { icp: null, campaign: null };
      if (isSameProgress(existing.campaign, data)) return state;
      return {
        ...state,
        [campaignId]: {
          ...existing,
          campaign: data,
        },
      };
    }

    case CLEAR_PROGRESS: {
      const { campaignId } = action.payload;
      if (!(campaignId in state)) return state; // nothing to clear -> no new ref
      const newState = { ...state };
      delete newState[campaignId];
      return newState;
    }

    case CLEAR_ALL_PROGRESS:
      return initialState;

    default:
      return state;
  }
};
